/**
 * RelaunchComposer — Cadre de relance d'un débat terminé.
 *
 * Affiché à côté de "Demander le Mémo du Conseil" à la fin d'une saisine.
 * Permet d'ajouter une question ET/OU des pièces jointes ; soumet
 *   POST /api/chat/:parentId/relaunch { message, attachments?, mode: "full" }
 * puis navigue vers /deliberation/:conversation_id.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  SaisineAttachments,
  type SaisineAttachmentsHandle,
  type AttachmentManifest,
  type PendingItem,
} from "@/components/qorum/SaisineAttachments";
import {
  DebateQuotaDialog,
  extractQuotaPayload,
  type DebateQuotaPayload,
} from "@/components/qv3/DebateQuotaDialog";
import {
  getCheckoutStash,
  clearCheckoutStash,
  setCheckoutStash,
  wasJustPaid,
  clearJustPaid,
} from "@/lib/checkout-stash";

const DEFAULT_MSG_WITH_DOCS =
  "De nouvelles pièces sont versées au débat — reprenez vos positions à leur lumière.";

interface Props {
  parentId: string;
}

export function RelaunchComposer({ parentId }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [items, setItems] = useState<PendingItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [quota, setQuota] = useState<DebateQuotaPayload | null>(null);
  const [paymentPending, setPaymentPending] = useState(false);
  const attachmentsRef = useRef<SaisineAttachmentsHandle>(null);

  // Hydrate depuis le stash de checkout (retour Stripe sur ce débat)
  useEffect(() => {
    const stash = getCheckoutStash();
    if (stash?.convId === parentId) {
      if (stash.pendingQuestion) setText(stash.pendingQuestion);
      clearCheckoutStash();
      toast.success("Paiement confirmé — votre débat est crédité.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  const ready = items.filter((p) => p.status === "done" && p.manifest);
  const hasText = text.trim().length > 0;
  const hasDocs = ready.length > 0;
  const uploading = items.some((p) => p.status === "uploading");
  const canSubmit = !submitting && !uploading && !paymentPending && (hasText || hasDocs);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const message = hasText ? text.trim() : DEFAULT_MSG_WITH_DOCS;
    const attachments: AttachmentManifest[] = ready.map((p) => p.manifest!);

    setSubmitting(true);
    try {
      const res = await apiFetch<{
        conversation_id: string;
        attachments_accepted?: number;
        attachments_rejected?: { filename?: string; reason?: string }[];
      }>(`/api/chat/${parentId}/relaunch`, {
        method: "POST",
        body: JSON.stringify({
          message,
          attachments: attachments.length > 0 ? attachments : undefined,
          mode: "full",
        }),
      });

      if (res.attachments_rejected && res.attachments_rejected.length > 0) {
        toast.warning(
          `${res.attachments_rejected.length} pièce(s) refusée(s) par le greffe.`,
        );
      }

      if (!res.conversation_id) {
        toast.error("Réponse inattendue du Conseil.");
        return;
      }

      navigate({
        to: "/deliberation/$id",
        params: { id: res.conversation_id },
      });
    } catch (err) {
      const quotaPayload = extractQuotaPayload(err);
      if (quotaPayload) {
        // Race webhook : l'user vient de payer, le crédit n'est pas encore
        // posé. On NE rouvre PAS le dialog d'achat — on attend la propagation.
        if (wasJustPaid()) {
          setPaymentPending(true);
          // Poll léger ~5s (pricing endpoint), puis on rend la main.
          let attempts = 0;
          const poll = async () => {
            attempts += 1;
            try {
              await apiFetch("/api/pricing/debate");
            } catch { /* ignore */ }
            if (attempts >= 3) {
              clearJustPaid();
              setPaymentPending(false);
              return;
            }
            setTimeout(poll, 1500);
          };
          setTimeout(poll, 1200);
        } else {
          setQuota(quotaPayload);
        }
      } else {
        const msg =
          err instanceof Error ? err.message : "Le Conseil ne peut être relancé.";
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
    <form
      className="qv3-relaunch"
      onSubmit={handleSubmit}
      aria-label="Relancer le Conseil avec un nouvel élément"
    >
      <style>{`
        .qv3-relaunch {
          width: 100%;
          max-width: 760px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 18px;
          border: 1px solid rgba(255,23,68, 0.4);
          background: rgba(255,23,68, 0.04);
          text-align: left;
        }
        .qv3-relaunch-label {
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-size: 10px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #FF1744;
          margin: 0;
        }
        .qv3-relaunch-textarea {
          width: 100%;
          min-height: 70px;
          resize: vertical;
          background: transparent;
          color: var(--qv3-paper, #f3efe6);
          border: 1px solid rgba(255,23,68, 0.35);
          padding: 12px 14px;
          font-family: var(--qv3-font-sans, system-ui, sans-serif);
          font-size: 14px;
          line-height: 1.5;
          outline: none;
          border-radius: 0;
          transition: border-color 200ms ease;
        }
        .qv3-relaunch-textarea::placeholder { color: var(--qv3-grey-500, #8a8580); font-style: italic; }
        .qv3-relaunch-textarea:focus { border-color: #FF1744; box-shadow: 0 0 0 2px rgba(255,23,68,0.25); }
        .qv3-relaunch-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .qv3-relaunch-btn {
          background: #FF1744;
          color: #FFFFFF;
          border: none;
          padding: 12px 22px;
          font-family: var(--qv3-font-mono, ui-monospace, monospace);
          font-weight: 600;
          font-size: 12px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          cursor: pointer;
          transition: background 200ms ease, color 200ms ease;
        }
        .qv3-relaunch-btn:hover:not(:disabled),
        .qv3-relaunch-btn:focus-visible:not(:disabled) {
          background: #8f1422;
          color: #FFFFFF;
        }
        .qv3-relaunch-btn:focus-visible {
          outline: 2px solid #FFFFFF;
          outline-offset: 3px;
          box-shadow: 0 0 0 4px rgba(255,23,68,0.55);
        }
        .qv3-relaunch-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>

      <p className="qv3-relaunch-label">Relancer le Conseil</p>

      <textarea
        className="qv3-relaunch-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Une précision, un nouvel angle, un document à verser au débat…"
        disabled={submitting}
        maxLength={2000}
      />

      <SaisineAttachments
        ref={attachmentsRef}
        userId={user?.id ?? null}
        items={items}
        onChange={setItems}
      />

      <div className="qv3-relaunch-row">
        <span
          style={{
            fontFamily: "var(--qv3-font-mono, ui-monospace, monospace)",
            fontSize: 10,
            letterSpacing: "0.12em",
            color: paymentPending ? "#FF1744" : "var(--qv3-grey-500, #8a8580)",
          }}
          aria-live="polite"
        >
          {paymentPending
            ? "Validation du paiement en cours, un instant…"
            : hasDocs && !hasText
              ? "Aucune question saisie — un message par défaut sera envoyé."
              : "\u00a0"}
        </span>
        <button
          type="submit"
          className="qv3-relaunch-btn"
          disabled={!canSubmit}
        >
          {submitting ? "Convocation…" : paymentPending ? "Validation…" : "Relancer le Conseil"}
        </button>
      </div>
    </form>
    <DebateQuotaDialog
      payload={quota}
      onClose={() => setQuota(null)}
      onBeforeRedirect={() => {
        setCheckoutStash({ convId: parentId, pendingQuestion: text });
      }}
    />
    </>
  );
}

export default RelaunchComposer;
